import { useMemo, useState } from 'react';
import { Check, Loader2, Pencil, Wrench } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { useGetTools } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useConfig } from 'hooks/use-config';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';

type ToolTab = 'api' | 'mcp_server';

export function SkillToolsSection({
  selectedToolIds,
  onChange,
  disabled = false,
  popoverSide = 'right',
}: {
  selectedToolIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  popoverSide?: 'right' | 'bottom';
}) {
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const blocksKey = configurationSettings?.blocksKey || '';
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ToolTab>('api');
  const [hasOpenedMcpTab, setHasOpenedMcpTab] = useState(false);

  const { data: apiToolsData, isLoading: isApiToolsLoading } = useGetTools({
    tool_type: 'api',
    page: 1,
    page_size: 50,
    project_key: blocksKey,
  });
  const { data: mcpToolsData, isLoading: isMcpToolsLoading } = useGetTools(
    {
      tool_type: 'mcp_server',
      page: 1,
      page_size: 50,
      project_key: blocksKey,
    },
    { enabled: hasOpenedMcpTab || selectedToolIds.length > 0 }
  );

  const allTools = useMemo<ITool[]>(() => {
    const uniqueTools = new Map<string, ITool>();
    [...(apiToolsData?.tools || []), ...(mcpToolsData?.tools || [])].forEach((tool) => {
      uniqueTools.set(tool.id, tool);
    });
    return Array.from(uniqueTools.values()).sort((left, right) => left.name.localeCompare(right.name));
  }, [apiToolsData?.tools, mcpToolsData?.tools]);

  const apiTools = useMemo(
    () => [...(apiToolsData?.tools || [])].sort((left, right) => left.name.localeCompare(right.name)),
    [apiToolsData?.tools]
  );
  const mcpTools = useMemo(
    () => [...(mcpToolsData?.tools || [])].sort((left, right) => left.name.localeCompare(right.name)),
    [mcpToolsData?.tools]
  );

  const toolsById = useMemo(() => new Map(allTools.map((tool) => [tool.id, tool])), [allTools]);
  const visibleTools = activeTab === 'api' ? apiTools : mcpTools;
  const isVisibleToolsLoading = activeTab === 'api' ? isApiToolsLoading : isMcpToolsLoading;

  const selectedTools = selectedToolIds
    .map((toolId) => toolsById.get(toolId) || null)
    .filter((tool): tool is ITool => !!tool);
  const unknownSelectedToolIds = selectedToolIds.filter((toolId) => !toolsById.has(toolId));

  const toggleTool = (toolId: string) => {
    const nextIds = selectedToolIds.includes(toolId)
      ? selectedToolIds.filter((value) => value !== toolId)
      : [...selectedToolIds, toolId];
    onChange(nextIds);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && activeTab === 'mcp_server') {
      setHasOpenedMcpTab(true);
    }
  };

  const handleTabChange = (value: string) => {
    const nextTab = value as ToolTab;
    setActiveTab(nextTab);
    if (nextTab === 'mcp_server') {
      setHasOpenedMcpTab(true);
    }
  };

  return (
    <div className="flex min-h-0 flex-col">
      <div className="mb-1 flex shrink-0 items-center justify-between pl-2 pr-1">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {t('CREATE_SKILL_TOOLS_LABEL') || 'Tools'}
        </p>
        {disabled ? null : (
          <Popover open={isOpen} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={t('SKILL_DETAILS_EDIT_TOOLS') || 'Edit tools'}
                data-testid="skill-detail-edit-tools"
                className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Pencil className="h-[13px] w-[13px]" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              side={popoverSide}
              sideOffset={8}
              className="w-[280px] p-0"
              onCloseAutoFocus={(event) => event.preventDefault()}
              onOpenAutoFocus={(event) => event.preventDefault()}
              onWheel={(event) => event.stopPropagation()}
            >
              <Command className="overflow-hidden">
                <CommandInput
                  placeholder={t('CREATE_SKILL_TOOLS_SEARCH') || 'Search tools...'}
                />
                <Tabs value={activeTab} onValueChange={handleTabChange}>
                  <div className="border-b border-border px-3 py-2">
                    <TabsList className="grid h-8 w-full grid-cols-2 rounded-[6px] border border-border bg-muted/40 p-0.5 shadow-none">
                      <TabsTrigger value="api" className="h-full flex-1 rounded-[4px] px-0 text-xs font-medium">
                        {t('TOOLS_TAB_API') || 'API'}
                      </TabsTrigger>
                      <TabsTrigger
                        value="mcp_server"
                        className="h-full flex-1 rounded-[4px] px-0 text-xs font-medium"
                      >
                        {t('TOOLS_TAB_MCP') || 'MCP'}
                      </TabsTrigger>
                    </TabsList>
                  </div>
                </Tabs>
                <CommandList className="max-h-[240px] overscroll-contain">
                  <CommandEmpty>
                    {isVisibleToolsLoading
                      ? t('CREATE_SKILL_TOOLS_LOADING') || 'Loading tools...'
                      : t('CREATE_SKILL_TOOLS_NOT_FOUND') || 'No tools found.'}
                  </CommandEmpty>
                  <CommandGroup>
                    {isVisibleToolsLoading ? (
                      <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('CREATE_SKILL_TOOLS_LOADING') || 'Loading tools...'}
                      </div>
                    ) : (
                      visibleTools.map((tool) => {
                        const isSelected = selectedToolIds.includes(tool.id);

                        return (
                          <CommandItem
                            key={tool.id}
                            value={`${tool.name} ${tool.description || ''} ${tool.type}`}
                            onSelect={() => toggleTool(tool.id)}
                            onPointerDown={(event) => event.preventDefault()}
                            className="cursor-pointer items-center gap-2 py-1.5"
                          >
                            <span className="min-w-0 flex-1 truncate text-sm">{tool.name}</span>
                            <Check
                              className={cn('h-4 w-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
                            />
                          </CommandItem>
                        );
                      })
                    )}
                  </CommandGroup>
                </CommandList>
                <div className="border-t border-border px-2 py-1.5">
                  <button
                    type="button"
                    className="w-full rounded-md px-2 py-1.5 text-left text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
                    disabled={selectedToolIds.length === 0}
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => onChange([])}
                  >
                    {t('CLEAR_ALL') || 'Clear all'}
                  </button>
                </div>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>

      <div className="flex min-h-0 flex-col gap-0.5 overflow-y-auto">
        {selectedTools.map((tool) => (
          <div key={tool.id} className="flex items-center gap-2 rounded px-2 py-1.5">
            <div className="flex size-5 min-w-5 items-center justify-center overflow-hidden rounded-md bg-background ring-1 ring-border">
              <Wrench className="size-3 min-w-3 text-muted-foreground" />
            </div>
            <span className="flex-1 truncate text-xs text-foreground/80">{tool.name}</span>
          </div>
        ))}
        {unknownSelectedToolIds.map((toolId) => (
          <div key={toolId} className="flex items-center gap-2 rounded px-2 py-1.5">
            <div className="flex size-5 min-w-5 items-center justify-center overflow-hidden rounded-md bg-background ring-1 ring-border">
              <Wrench className="size-3 min-w-3 text-muted-foreground" />
            </div>
            <span className="flex-1 truncate text-xs text-foreground/80">{toolId}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
